import type { Binding } from "../types";

/**
 * Shortcuts that already work, dispatched by TipTap extensions or ProseMirror
 * plugins rather than by us.
 *
 * They are registered so the registry reflects reality: their combinations are
 * reserved against future collisions, and the help UI can list them without a
 * second hand-maintained table that would immediately start drifting.
 *
 * `run` is never called for these — `documentationOnly` keeps them out of
 * dispatch entirely.
 *
 * Not listed: the comment box's ⌘Enter submit. Comment and inline-chat editors
 * build their own restricted extension list, never become the active editor,
 * and so occupy no slot in any scope the registry dispatches — reserving one
 * for them would invent a collision that cannot happen.
 */
const noop = () => undefined;

function documented(
  id: string,
  keys: string,
  label: string,
  group: string,
  source: string,
): Binding {
  return {
    id,
    keys,
    label,
    group,
    source,
    scope: "editor",
    documentationOnly: true,
    run: noop,
  };
}

export const documentedBindings: Binding[] = [
  // Inherited from StarterKit and the mark extensions in defaultExtensions.
  documented("editor.bold", "Mod+B", "Bold", "Formatting", "StarterKit"),
  documented("editor.italic", "Mod+I", "Italic", "Formatting", "StarterKit"),
  documented("editor.underline", "Mod+U", "Underline", "Formatting", "TiptapUnderline"),
  documented("editor.strike", "Mod+Shift+S", "Strikethrough", "Formatting", "StarterKit"),
  documented("editor.code", "Mod+E", "Inline code", "Formatting", "StarterKit"),
  documented("editor.undo", "Mod+Z", "Undo", "History", "StarterKit"),
  documented("editor.redo", "Mod+Shift+Z", "Redo", "History", "StarterKit"),

  // Ours, dispatched from extensions/plugins.
  documented(
    "editor.aiHighlight",
    "Mod+Shift+H",
    "AI highlight",
    "Formatting",
    "ai-highlight extension",
  ),
  documented(
    "editor.selectBlock",
    "Mod+A",
    "Select block, then whole page",
    "Selection",
    "CustomKeymap extension",
  ),
  documented(
    "editor.deleteBlock",
    "Backspace",
    "Delete block",
    "Blocks",
    "boardDeletionGuard plugin",
  ),
];
